import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import Heading from '../../../generic-components/Heading';
import Button from '../../../generic-components/Button';
import { LinkItem } from './LinkItem';
import { UserMessage } from '../../../DesignSystem/LiliUserMessage/UserMessage';
import { useUnlinkAccountQuery } from '../../../hooks/query/useUnlinkAccountQuery';
import { useStore } from '../../../store';
import { useGenerateLinkToken } from '../../../hooks/query/useGenerateLinkToken';
import { UnLinkAccountConfirmation } from './UnLinkAccountConfirmation';
import { ILinkedAccount, IPostLinkData } from '../../../types';
import { TEXT, STYLE } from '../constants/index';
import { PlaidActionWrapper } from './PlaidActionWrapper';
import { FlowContext } from '../pages';
import { TopAlertBar } from '../../../generic-components/TopAlertBar';
import { trackMixPanel } from '../../../services/mixpanel.service';

const { PAGES: { POST_LINKED } } = TEXT;

const { PAGES: { POST_LINKED: POST_LINKED_STYLES } } = STYLE;

interface IProps {
    metaData: ILinkedAccount[];
    removeCheckingAccount: () => void;
    handlePlaidMetaData: (data: IPostLinkData) => void;
    handleActiveLinkOption: (data: {activeItemIdx: number | null, displayTitle: string}) => void;
    isFetchingLinkedAccounts: boolean;
}

export const PostLink = ({
  metaData, removeCheckingAccount, handlePlaidMetaData, handleActiveLinkOption, isFetchingLinkedAccounts,
}: IProps) => {
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [isUserMessage, setIsUserMessage] = useState<boolean>(false);
  const [selectedOptionIdx, setSelectedOptionIdx] = useState<number | null>(null);
  const [linkToken, setLinkToken] = useState<string>('');
  const [alertBar, setAlertBar] = useState<string>('');
  const { t } = useTranslation();
  const elLinksContainer = useRef<HTMLDivElement | null>(null);
  const { customerId } = useStore(state => state.user);
  const { routerHistory } = useStore(state => state);
  const isAddMoneyFlow = useContext(FlowContext);
  const {
    isLoading: isFetchLinkToken, refetch: fetchLinkToken,
  } = useGenerateLinkToken(customerId);

  const {
    refetch: initUnLinkAccount,
    isFetching: isLoading,
  } = useUnlinkAccountQuery(customerId, selectedOptionIdx !== null ? metaData[selectedOptionIdx].externalId : '');

  const handleEdit = () => {
    setIsEdit(prevState => !prevState);
    setSelectedOptionIdx(null);
    handleActiveLinkOption({ activeItemIdx: null, displayTitle: '' });
  };

  const removeLinkItem = () => {
    setIsUserMessage(true);
  };

  const handleUnlink = () => {
    if (selectedOptionIdx === null) return;
    initUnLinkAccount()
      .then(() => {
        trackMixPanel(
          'Unlink Plaid Account',
          {
            Origin: isAddMoneyFlow ? 'Add Money Screen' : 'Send Money Screen',
            'Bank Name ': metaData?.[selectedOptionIdx]?.institutionName || '',
            'Unlink Method': 'Edit',
          },
        );
        setIsUserMessage(false);
        setSelectedOptionIdx(null);
        removeCheckingAccount();
        setAlertBar('Account has been successfully removed');
      });
  };

  const handleUserMessageClick = () => {
    setIsUserMessage(false);
  };

  const handlePostLink = (data: IPostLinkData) => {
    handlePlaidMetaData(data);
  };

  const handleGenerateToken = () => {
    fetchLinkToken()
      .then(({ data }) => {
        const { linkToken: { link_token: linkTokenRes } } = data;
        setLinkToken(linkTokenRes);
      });
  };

  const generateLinkItemTitle = (idx: number) => (`${metaData[idx].institutionName} - ${metaData[idx].displayName}, ${metaData[idx].mask}`);

  const handleOptionSelection = (idx: number) => {
    setSelectedOptionIdx(idx);
    trackMixPanel(
      'Act on Plaid Institutions List',
      {
        Action: 'Select Account',
        'Bank Name': metaData?.[idx]?.institutionName || '',
      },
    );
    trackMixPanel(
      'Did Select Plaid Account',
      {
        ui_screen: isAddMoneyFlow ? 'Plaid Add Money' : 'Plaid Send Money',
      },
    );
    if (isEdit) return;
    handleActiveLinkOption({ activeItemIdx: idx, displayTitle: generateLinkItemTitle(idx) });
  };

  const handleTopBarClose = () => {
    setAlertBar('');
  };

  useEffect(() => {
    setIsEdit(false);
    elLinksContainer.current?.classList.remove('animate-fadeIn');
    setTimeout(() => {
      elLinksContainer.current?.classList.add('animate-fadeIn');
    }, 10);
  }, [metaData]);

  useEffect(() => {
    trackMixPanel(
      'show Plaid Institutions List',
      {
        Origin: routerHistory[routerHistory.length - 2] || 'Home',
      },
    );
  }, []);

  return (
    <>
      <Heading
        title={isAddMoneyFlow ? t(POST_LINKED.HEADING) : t(POST_LINKED.HEADING_SEND)}
        className={POST_LINKED_STYLES.HEADING}
      />
      <p className={POST_LINKED_STYLES.CONTENT}>
        {isAddMoneyFlow ? t(POST_LINKED.CONTENT) : t(POST_LINKED.CONTENT_SEND)}
      </p>
      <div className={POST_LINKED_STYLES.LIST_HEADING_CTA_WRAPPER}>
        <Heading title={t(POST_LINKED.SUB_HEADER)} className={POST_LINKED_STYLES.LIST_HEADING} />
        <Button onClick={handleEdit} title={isEdit ? t(POST_LINKED.EDIT_ACCOUNTS_LIST_CTA_MODE_B) : t(POST_LINKED.EDIT_ACCOUNTS_LIST_CTA_MODE_A)} className={POST_LINKED_STYLES.LIST_CTA} id='link-items-edit' />
      </div>
      {metaData && (
      <div ref={elLinksContainer} className={POST_LINKED_STYLES.LIST_WRAPPER}>
        { metaData.map((item: ILinkedAccount, idx: number) => (
          <LinkItem
            key={item.id}
            title={item?.officialName || item?.displayName}
            content={generateLinkItemTitle(idx)}
            isEdit={isEdit}
            idx={idx}
            isSelected={idx === selectedOptionIdx}
            isLastOption={idx === metaData.length - 1}
            removeLinkItem={removeLinkItem}
            onClick={handleOptionSelection}
          />
        ))}

      </div>
      )}

      {!isEdit && <PlaidActionWrapper shouldAutoOpenLink={!metaData.length} shouldInvertLoaderColor linkTitle={t(POST_LINKED.PLAID_CTA)} linkStyle={POST_LINKED_STYLES.PLAID_CTA} linkToken={linkToken} isLoading={isLoading || isFetchingLinkedAccounts} isFetchLinkToken={isFetchLinkToken} callbackHandler={handleGenerateToken} handlePostLink={handlePostLink} />}

      {(isUserMessage && selectedOptionIdx !== null) && (
      <UserMessage onClick={handleUserMessageClick} id='unlink-account-modal'>

        <UnLinkAccountConfirmation
          title={generateLinkItemTitle(selectedOptionIdx)}
          isLoading={isLoading}
          handleUnlink={handleUnlink}
          handleUserMessageClick={handleUserMessageClick}
        />

      </UserMessage>
      )}
      {alertBar && <TopAlertBar autoDismiss isError={false} content={alertBar} onClose={handleTopBarClose} /> }
    </>
  );
};
